import { useEffect, useState, useMemo, useCallback } from 'react';
import { Button, CircularProgress, Drawer, IconButton, Portal, Typography } from '@mui/material';
import { DrawerHeader, DrawerFooter, CentralDataGridPro, useSnackbar, Ico_Info, Ico_Repeat } from '@sagecentral/core-ui';
import { IRepeatTaskInstance, IRepeatTaskPayload } from 'models/tasks';
import { useTaskContext, useTaskRepeatTasks } from 'hooks/tasks';
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid';
import Columns from './RepeatTaskColumns';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { IWorkItemWorkflowTask } from 'models/work-items/IWorkItemWorkflow';
import { ITaskContext } from 'hooks/tasks/useTaskContext';

type AddRepeatTasksDrawerProps = {
    task?: IWorkItemWorkflowTask | null;
    onTaskAdded?: () => void;
};

const usePropsTaskContext = (propTask: IWorkItemWorkflowTask | null | undefined): ITaskContext => {
    const originalContext = useTaskContext();
    
    return useMemo(() => {
        if (!propTask) {
            return originalContext;
        }
        
        return {
            ...originalContext,
            task: propTask as any,
        };
    }, [originalContext, propTask]);
};

const AddRepeatTasksDrawer = (props: AddRepeatTasksDrawerProps) => {
    const { task, onTaskAdded } = props;
    const { displaySnackbar } = useSnackbar();

    const context = usePropsTaskContext(task);
    const apiRef = useGridApiRef();

    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<GridRowSelectionModel>([]);

    const stableRefresh = useCallback(() => {
        if (onTaskAdded) {
            onTaskAdded();
        }
    }, [onTaskAdded]);

    const handleSaveComplete = useCallback((success: boolean, message?: string) => {
        if (success) {
            if (context.task) {
                context.updateTaskOverview({
                    ...context.task,
                    isRepeatable: false
                });
            }
        
            setOpen(false);
            displaySnackbar('success', 'Repeat Task(s) added to workflow.');
            
            setTimeout(() => {
                stableRefresh();
            }, 0);
        } else {
            displaySnackbar('error', `Repeat Task(s) failed to be added${message ? ` - ${message}` : ''}.`);
        }
    }, [context, displaySnackbar, setOpen, stableRefresh])

    const [data, total, loading, addRepeatTasks, working] = useTaskRepeatTasks(context.task?.id ?? 0, open, handleSaveComplete);

    useEffect(() => {
        if (data === null) return;
        setSelected(data.filter(task => task.mandatory).map(task => task.id));
    }, [data]);

    const handleClose = () => setOpen(false);
    const handleConfirm = useCallback(() => {
        const { current: api } = apiRef;
        if (api === null) return;

        const payload = selected.map<IRepeatTaskPayload>(id => {
            return {
                id: Number(id.toString()),
                duration: api?.getCellValue(id, 'duration'),
                responsibilityId: api?.getCellValue(id, 'responsibilityId'),
                resourceId: api?.getCellValue(id, 'resourceId')
            };
        });
        
        addRepeatTasks(payload);
    }, [apiRef, selected]);

    const checkIsRowSelectable = (params: GridRowParams<IRepeatTaskInstance>) => !params.row.mandatory;

    const handleSelectedChange = (model: GridRowSelectionModel) => {
        if (data !== null) {
            const updatedModel = [...model];
            for (let task of data) {
                if (!task.mandatory) continue;
                
                const index = updatedModel.findIndex(id => task.id === id);
                if (index === -1) {
                    updatedModel.push(task.id);
                }
            }
            setSelected(updatedModel);
        }                         
    };

    const handleRowCommit = (newValue: IRepeatTaskInstance, oldValue: IRepeatTaskInstance) => {
        if (newValue.responsibilityId !== oldValue.responsibilityId) {
            const responsibility = newValue.responsibilities.find(r => r.id === newValue.responsibilityId);
            if (responsibility) {
                const resource = responsibility.resources.find(r => r.isDefault);
                if (resource) {
                    newValue.resourceId = resource.id;
                } else {
                    newValue.resourceId = null;
                }
            }            
        }

        if (data !== null) {
            for (let task of data) {
                if(task.id === newValue.id){
                    task.duration = newValue?.duration;
                    task.responsibilityId = newValue?.responsibilityId;
                    task.resourceId = newValue?.resourceId;
                }
            }
                                 
        }      
        return newValue;
    };

    const disableConfirm = working || selected.length === 0;

    return (<>
        { context.task?.isRepeatable && !task ? <Portal container={context.btnContainer}>
            <Button
                variant='outlined'
                color='secondary'
                onClick={() => setOpen(true)}
            >
                Add Repeat Tasks
            </Button>
        </Portal> :
        context.task?.isRepeatable && task ?
        <><IconButton onClick={() => setOpen(true)}
                tabIndex={0}
                aria-label="Repeat Task" >
            <svg height={18} width={18} viewBox="0 0 22 22" fill="none" >
                    <Ico_Repeat sx={{ stroke: '#001A69' }} />
            </svg>
            </IconButton></>
        : null}
        <Drawer
            anchor='right'
            open={open}
            onClose={() => setOpen(false)}
        >
            <DrawerHeader title='Add Repeat Tasks' onClose={handleClose} />
            <Typography variant='body1' mb={3}>
                <b>Please note:</b> These Repeat Task(s) will immediately be added to the workflow and <b>you will not be able to undo the action.</b>
            </Typography>
            <CentralDataGridPro apiRef={apiRef}
                sx={{
                    '& .MuiDataGrid-columnHeaderCheckbox': {
                        padding: 0
                    },
                    '& .MuiDataGrid-row': {
                        padding: 0
                    }
                }}
                rows={data ?? []}
                getRowId={(r) => r.id}
                rowCount={total}
                loading={loading || working}
                columns={Columns}
                filterMode="client"
                sortingMode="client"
                checkboxSelection                
                rowSelectionModel={selected}
                isRowSelectable={checkIsRowSelectable}
                onRowSelectionModelChange={handleSelectedChange}
                disableRowSelectionOnClick
                processRowUpdate={handleRowCommit}
                slots={{
                    loadingOverlay: () => (<CircularProgress/>),
                }}
            />
            <DrawerFooter
                onCancel={handleClose}
                disableConfirm={disableConfirm}
                confirmText='Add To Workflow'
                onConfirm={handleConfirm}
            />
        </Drawer>
    </>);
};

export default AddRepeatTasksDrawer;